import { format, isValid, parse } from 'date-fns';

import type { FilterConfig, SelectOption } from './types';

import { toText } from '@/helpers';

import {
  DISPLAY_DATES_FORMAT,
  DISPLAY_MONTH_FORMAT,
  SEND_DATES_FORMAT,
} from '@/constants/dates';

export const getOptionName = (
  options: SelectOption[] | undefined,
  id: unknown,
) =>
  options?.find((option) => String(option.id) === String(id))?.name ??
  String(id);

export const formatDateValue = (
  raw: string,
  pattern: string,
  display: string,
) => {
  const date = parse(raw, pattern, new Date());
  return isValid(date) ? format(date, display) : raw;
};

export const hasValue = (value: unknown) =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && value !== '';

export const getAppliedValue = (filter: FilterConfig) =>
  filter.appliedValue === undefined ? filter.value : filter.appliedValue;

export const getValueTexts = (
  filter: FilterConfig,
  booleanLabels: { yes: string; no: string },
): string[] => {
  const { type } = filter;
  const options = filter.options as SelectOption[] | undefined;
  const value = getAppliedValue(filter);

  if (value === null || value === undefined || value === '') {
    return [];
  }

  switch (type) {
    case 'select':
      return [getOptionName(options, value)];
    case 'multiselect':
      return (value as unknown[]).map((id) => getOptionName(options, id));
    case 'boolean':
      return [value ? booleanLabels.yes : booleanLabels.no];
    case 'date':
      return [
        formatDateValue(toText(value), SEND_DATES_FORMAT, DISPLAY_DATES_FORMAT),
      ];
    case 'month':
      return [
        formatDateValue(
          `${toText(value)}-01`,
          SEND_DATES_FORMAT,
          DISPLAY_MONTH_FORMAT,
        ),
      ];
    case 'autocomplete':
      return [filter.getOptionLabel?.(value) ?? toText(value)];
    case 'text':
      return [toText(value)];
    default:
      return [];
  }
};
